import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Badge, Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import DateRangePresets from '../components/DateRangePresets';
import EmptyState from '../components/EmptyState';
import ErrorRetry from '../components/ErrorRetry';
import TableSkeleton from '../components/TableSkeleton';
import QuoteConversionCard from '../components/QuoteConversionCard';
import QuoteDetailModal from '../components/QuoteDetailModal';
import ExportButtons from '../components/ExportButtons';
import type { ExportColumn } from '../lib/exportTable';
import { fetchQuotes } from '../api/quotes';
import { useStoreFilter } from '../lib/storeFilter';
import { usePagination } from '../lib/usePagination';
import Pagination from '../components/Pagination';
import { localYMD } from '../lib/format';
import { t } from '../lib/i18n';
import type { Quote, QuoteAging, User } from '../api/types';
import DrillCell from '../components/DrillCell';

const fmtMxn = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 });

function dateNDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return localYMD(d);
}

function agingBadge(a: QuoteAging) {
  const color = a === 'fresh' ? 'emerald' : a === 'aging' ? 'amber' : 'red';
  return <Badge color={color}>{t.quotes.aging[a]}</Badge>;
}

// Export column spec — same labels as the table; aging exported as its label.
const QUOTES_EXPORT: ExportColumn<Quote>[] = [
  { label: t.quotes.cols.cdate,   value: (r) => r.cdate },
  { label: t.quotes.cols.tnum,    value: (r) => r.tnum, numeric: true },
  { label: t.quotes.cols.account, value: (r) => (r.anum > 0 ? `${r.anum}${r.anum_name ? ' · ' + r.anum_name : ''}` : '') },
  { label: t.quotes.cols.store,   value: (r) => r.sid, numeric: true },
  { label: t.quotes.cols.slid,    value: (r) => r.slid },
  { label: t.quotes.cols.lines,   value: (r) => r.lines, numeric: true },
  { label: t.quotes.cols.subt,    value: (r) => r.subt_mxn, numeric: true, pdf: (r) => fmtMxn.format(r.subt_mxn) },
  { label: t.quotes.cols.days,    value: (r) => r.days_open, numeric: true },
  { label: t.quotes.cols.aging,   value: (r) => t.quotes.aging[r.aging] },
];

export default function SalesQuotes() {
  const user = useOutletContext<User>();
  const [from, setFrom] = useState(dateNDaysAgo(90));
  const [to, setTo] = useState(dateNDaysAgo(0));
  const [slid, setSlid] = useState('');
  const [aging, setAging] = useState<QuoteAging | 'all'>('all');
  // Shared header store (lib/storeFilter); SalesLayout renders the picker.
  const [sid] = useStoreFilter(user);
  const [rows, setRows] = useState<Quote[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [open, setOpen] = useState<{ sid: number; tnum: number } | null>(null);
  const pg = usePagination(rows);

  const load = () => {
    setErr(null);
    setRows(null);
    fetchQuotes({ from, to, sid, slid: slid || undefined, aging })
      .then(setRows)
      .catch((e: Error) => setErr(e.message ?? String(e)));
  };
  useEffect(() => { load(); }, [from, to, slid, aging, sid]);

  const kpis = useMemo(() => {
    if (!rows) return { count: 0, total: 0, fresh: 0, aging: 0, stale: 0 };
    let total = 0, fresh = 0, agingC = 0, stale = 0;
    for (const r of rows) {
      total += r.subt_mxn;
      if (r.aging === 'fresh') fresh++;
      else if (r.aging === 'aging') agingC++;
      else stale++;
    }
    return { count: rows.length, total, fresh, aging: agingC, stale };
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <Title>{t.quotes.title}</Title>
          <Text className="text-tremor-content text-sm">{t.quotes.subtitle}</Text>
        </div>
        <ExportButtons
          title={t.quotes.title}
          filename={`Cotizaciones ${from} ${to}`}
          meta={[
            `${from} – ${to}`,
            ...(sid !== null ? [`${t.filter.store} ${sid}`] : []),
            ...(aging !== 'all' ? [`${t.quotes.cols.aging}: ${t.quotes.aging[aging]}`] : []),
            ...(slid ? [`${t.quotes.cols.slid}: ${slid}`] : []),
          ]}
          printedBy={user.username}
          columns={QUOTES_EXPORT}
          rows={rows}
        />
      </div>

      <Card className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Kpi label={t.quotes.kpi.open}   value={String(kpis.count)} />
        <Kpi label={t.quotes.kpi.total}  value={fmtMxn.format(kpis.total)} />
        <Kpi label={t.quotes.aging.fresh} value={String(kpis.fresh)} />
        <Kpi label={t.quotes.aging.aging} value={String(kpis.aging)} />
        <Kpi label={t.quotes.aging.stale} value={String(kpis.stale)} />
      </Card>

      <QuoteConversionCard />

      <Card className="space-y-3">
        <DateRangePresets from={from} to={to} onChange={({ from: f, to: t2 }) => { setFrom(f); setTo(t2); }} />
        <div className="flex flex-wrap gap-3 items-end">
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.quotes.cols.slid}</div>
            <TextInput value={slid} onValueChange={setSlid} placeholder="slid" className="w-32" />
          </label>
          <label className="text-sm">
            <div className="text-tremor-content text-xs mb-1">{t.quotes.cols.aging}</div>
            <select
              value={aging}
              onChange={(e) => setAging(e.target.value as typeof aging)}
              className="w-40 rounded-tremor-default border border-tremor-border bg-tremor-background py-2 px-3 text-sm text-tremor-content-strong focus:border-tremor-brand focus:outline-none"
            >
              <option value="all">{t.quotes.aging.all}</option>
              <option value="fresh">{t.quotes.aging.fresh}</option>
              <option value="aging">{t.quotes.aging.aging}</option>
              <option value="stale">{t.quotes.aging.stale}</option>
            </select>
          </label>
        </div>
      </Card>

      {err && <ErrorRetry message={err} onRetry={load} />}
      {!err && rows === null && <TableSkeleton rows={6} cols={9} />}
      {!err && rows !== null && rows.length === 0 && <EmptyState message={t.quotes.empty} />}
      {!err && rows !== null && rows.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.quotes.cols.cdate}</TableHeaderCell>
                <TableHeaderCell>{t.quotes.cols.tnum}</TableHeaderCell>
                <TableHeaderCell>{t.quotes.cols.account}</TableHeaderCell>
                <TableHeaderCell>{t.quotes.cols.store}</TableHeaderCell>
                <TableHeaderCell>{t.quotes.cols.slid}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.quotes.cols.lines}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.quotes.cols.subt}</TableHeaderCell>
                <TableHeaderCell className="text-right">{t.quotes.cols.days}</TableHeaderCell>
                <TableHeaderCell>{t.quotes.cols.aging}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pg.pageRows.map((r) => (
                <TableRow
                  key={`${r.sid}-${r.tnum}`}
                  className="cursor-pointer hover:bg-tremor-background-muted"
                  onClick={() => setOpen({ sid: r.sid, tnum: r.tnum })}
                >
                  <TableCell>{r.cdate}</TableCell>
                  <TableCell>{r.tnum}</TableCell>
                  <TableCell>
                    {r.anum > 0 ? (
                      <span onClick={(e) => e.stopPropagation()}>
                        <DrillCell target={{ kind: 'account', anum: r.anum }}>
                          {r.anum} {r.anum_name && `· ${r.anum_name}`}
                        </DrillCell>
                      </span>
                    ) : '—'}
                  </TableCell>
                  <TableCell>{r.sid}</TableCell>
                  <TableCell>{r.slid || '—'}</TableCell>
                  <TableCell className="text-right">{r.lines}</TableCell>
                  <TableCell className="text-right">{fmtMxn.format(r.subt_mxn)}</TableCell>
                  <TableCell className="text-right">{r.days_open}</TableCell>
                  <TableCell>{agingBadge(r.aging)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination {...pg} className="px-4" />
        </Card>
      )}

      {open && (
        <QuoteDetailModal sid={open.sid} tnum={open.tnum} onClose={() => setOpen(null)} />
      )}
    </div>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-tremor-content text-xs">{label}</div>
      <div className="text-tremor-content-strong text-lg font-semibold">{value}</div>
    </div>
  );
}
